// Lists signup-page variants. Each row links to /signups/:id for editing.
// "New signup page" button creates a row via /signups/new.

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, MailPlus, Home, Trash2, MoreHorizontal, ExternalLink } from 'lucide-react';
import { signupPagesApi } from '../lib/api';
import type { SignupPageListItem } from '@shared/types';

const HOMEPAGE_SLUG = 'homepage';

export default function SignupPagesListPage() {
  const [pages, setPages] = useState<SignupPageListItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const navigate = useNavigate();

  async function load() {
    try {
      setErr(null);
      const { pages } = await signupPagesApi.list();
      // Pin the homepage row to the top.
      const homepage = pages.find((p) => p.slug === HOMEPAGE_SLUG);
      const others = pages.filter((p) => p.slug !== HOMEPAGE_SLUG);
      setPages(homepage ? [homepage, ...others] : pages);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className="max-w-4xl mx-auto py-6 px-4 sm:py-10 sm:px-6">
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl text-slate-900">Signup pages</h1>
            <p className="text-sm text-slate-500 mt-1">
              Variants of <code className="text-xs bg-slate-100 px-1 py-0.5 rounded break-all">penobscotmorning.com/signup/&lt;slug&gt;</code> with their own copy and welcome email.
            </p>
          </div>
          <button onClick={() => navigate('/signups/new')} className="btn-primary self-start sm:self-auto">
            <Plus size={14} /> New signup page
          </button>
        </header>

        {err && <p className="text-sm text-red-600 mb-4">{err}</p>}

        {pages === null ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : pages.length === 0 ? (
          <EmptyState onCreate={() => navigate('/signups/new')} />
        ) : (
          <ul className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
            {pages.map((p) => (
              <Row key={p.id} page={p} onChange={load} />
            ))}
          </ul>
        )}

        <div className="mt-8">
          <Link to="/subscribers" className="text-sm text-blue-700 hover:underline">
            View all subscribers →
          </Link>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="border-2 border-dashed border-slate-200 rounded-lg p-12 text-center">
      <MailPlus size={28} className="mx-auto text-slate-300 mb-2" />
      <p className="text-sm font-medium text-slate-700">No signup pages yet</p>
      <p className="text-xs text-slate-500 mt-1 mb-4">
        Create a variant to track signups from a specific source.
      </p>
      <button onClick={onCreate} className="btn-primary py-2 px-4 mx-auto">
        <Plus size={14} /> Create your first signup page
      </button>
    </div>
  );
}

function Row({ page, onChange }: { page: SignupPageListItem; onChange: () => Promise<void> }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const isHomepage = page.slug === HOMEPAGE_SLUG;
  const publicUrl = isHomepage
    ? 'https://penobscotmorning.com/'
    : `https://penobscotmorning.com/signup/${page.slug}`;
  const Icon = isHomepage ? Home : MailPlus;

  async function del(e: React.MouseEvent) {
    e.stopPropagation();
    if (!confirm(`Delete signup page "${page.title}" (slug: ${page.slug})? Existing subscribers keep their attribution.`)) return;
    setBusy(true);
    try {
      await signupPagesApi.delete(page.id);
      await onChange();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
      setMenuOpen(false);
    }
  }

  return (
    <li className="relative group">
      <button
        type="button"
        onClick={() => navigate(`/signups/${page.id}`)}
        disabled={busy}
        className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-slate-50 disabled:opacity-50"
      >
        <Icon size={16} className={isHomepage ? 'text-blue-700 flex-shrink-0' : 'text-slate-400 flex-shrink-0'} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium truncate ${page.active ? 'text-slate-900' : 'text-slate-400 line-through'}`}>
              {isHomepage ? 'Homepage' : page.title}
            </span>
            {!page.active && !isHomepage && (
              <span className="text-[10px] uppercase tracking-wide font-semibold bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">
                inactive
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5 truncate">
            <code className="text-[11px] bg-slate-100 px-1 py-0.5 rounded">{isHomepage ? '/' : `/signup/${page.slug}`}</code>
            <span className="ml-2">{page.subscriber_count} subscriber{page.subscriber_count === 1 ? '' : 's'}</span>
          </div>
        </div>
        <a
          href={publicUrl}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="p-1.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600"
          title="Open public URL"
        >
          <ExternalLink size={14} />
        </a>
        {!isHomepage && (
          <span
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen((o) => !o); }}
            className="p-1.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 cursor-pointer"
          >
            <MoreHorizontal size={14} />
          </span>
        )}
      </button>
      {menuOpen && !isHomepage && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setMenuOpen(false)} />
          <div className="absolute right-3 top-12 z-30 bg-white border border-slate-200 rounded-md shadow-lg py-1 w-44">
            <button onClick={del} className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
              <Trash2 size={12} /> Delete
            </button>
          </div>
        </>
      )}
    </li>
  );
}
